import { Container } from "unstated";
import { EventEmitter } from "fbemitter";
import moment from "moment";
import _ from "lodash";
import momentLocalizer from "react-widgets-moment";
import appEnum, { SavingState } from "../util/appEnum"
import commonHelper from "../util/commonHelper"
import { ServiceUser } from "../services/service-user";
import { serviceOrganization } from "../services/service-organization";
import { ServiceProjects } from "../services/service-projects";
import * as Sentry from "@sentry/react";
import { autosave } from "../util";

export class AppStateContainer extends Container {
  currentOrganization;
  organizations;
  orgSlug;
  avatarUrlFetched=false;
  emitter = new EventEmitter();
  state = {
    tmpToken: this.loadObj("tmpToken"),
    token: this.loadObj("token"),
    organizations: null,
    currentOrganization: null,
    loaded: false,
    language: this.normalizeLanguageCode(this.loadString('language') || 'sv'),
    editingTranslationId: null,
    editingTranslationDefault: null,
    messages: {},
    supplierToken: null,
    supplierApplicationCount: 0,
    autoSavingState: SavingState.HIDDEN,
    showMassMessagingModal: false,
    globalNotifications: {
      project: 0,
      internalChat: 0,
    },
    logoUrl: '',
    productId: null,
    anonymousUUID: '',
  };
  #timeout = undefined;

  normalizeLanguageCode(code) {
    return code?.slice(0,2);
  }

  persistObj(key, obj) {
    if (obj === undefined) localStorage.removeItem(key);
    localStorage.setItem(key, JSON.stringify(obj));
  }

  loadObj(key) {
    const getLocalItem = localStorage.getItem(key);
    return (!getLocalItem || getLocalItem === "undefined" || getLocalItem === "null") ? null : JSON.parse(getLocalItem);
  }

  persistString(key, value) {
    localStorage.setItem(key, value);
  }

  loadString(key) {
    return localStorage.getItem(key);
  }

  constructor() {
    super();

    this.setState({ loaded: true }, () => {
      moment.locale(this.state.language);
      momentLocalizer();
    });
  }

  async setSupplierToken({userData, organizationData}) {
    if (organizationData){
      this.setState({
        organizations: organizationData,
        currentOrganization: organizationData[0],
      })
      this.currentOrganization = organizationData[0]
      this.orgSlug = `/${organizationData[0].slug}`;
    }
    this.setState({ supplierToken: userData });
  }

  async setToken({userData, organizationData}, setUrl) {
    this.persistObj("token", userData);
    if (organizationData){
      this.persistObj("organizations", organizationData);
      this.currentOrganization = organizationData[0]
      this.orgSlug = `/${organizationData[0].slug}`;
    }

    await this.setState({ token: userData },() => {
      this.setAvatarFetchedState(false);
    });

    if (setUrl) window.location.href = `${this.orgSlug}/dashboard`;
  }

  setTheme(theme) {
    this.persistObj("theme", theme);
  }

  getTheme() {
    return JSON.parse(localStorage.getItem("theme"));
  }

  async setTokenLocalStorage({userData, organizationData,slugFromUrl}) {
    this.persistObj("token", {...this.loadObj("token"),...userData});
    this.setState((prevState) => ({
      token: {...prevState.token,...userData}
    }))
    if (organizationData){
      this.persistObj("organizations", organizationData);
      this.currentOrganization = organizationData[0]
    }
  }

  async setHvdTokenInMemory(hvdToken) {
    await this.setState((prevState) => ({
      token: { ...prevState.token, hvdToken },
    }));
  }

  clearToken() {
    this.setToken({});
    localStorage.removeItem('token');
    localStorage.removeItem('organizations');
    localStorage.removeItem('currentOrganization');
    localStorage.removeItem('theme');
    Sentry.configureScope(scope => scope.setUser(null));
  }

  async setLoginAsToken(token) {
    const tmpToken = this.state.token;
    this.state.token.route = undefined;
    this.persistObj("tmpToken", this.state.token);
    this.persistObj("token", token);
    await this.setState({ token, tmpToken });
  }

  async setLanguage(lang) {
    this.persistString('language', lang);
    moment.locale(lang);
    momentLocalizer();
    this.setState({ language: lang }, () => {
      this.emitter.emit("onLanguageChange");
    });
  }

  async logout() {
    const tmpToken = this.state.tmpToken;
    if (tmpToken) {
      this.persistObj("tmpToken", null);
      await this.setToken(tmpToken);
      await this.setState({ tmpToken: null });
    } else {
      await this.setToken({});
    }
  }
  signout(){
    this.clearToken()
  }

  getRoleFromUrlParams() {
    const historyRole = _.get(window, "history.state.state.role");
    return historyRole || commonHelper.getQueryStringArray().r;
  }

  getUserRole = () => this.getLoggedInUserDetail()?.role;

  getUserId = () => this.getLoggedInUserDetail()?.id;

  getUserEmail = () => this.getLoggedInUserDetail()?.email;

  isSuperAdmin = () => !!this.state?.token?.isSuperAdmin || this.getUserRole() == appEnum.Role.SuperAdmin;

  superAdminOrgAndUser = () => this.isSuperAdmin() && this.getCurrentOrgSlug() === 'superadmin';

  isKalkylRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Kalkyl;
  }

  isInkopRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Inköp;
  }
  isClientRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Client;
  }
  isSupplierRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Supplier;
  }
  isWriteAccessRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Admin || role == appEnum.Role.Inköp || role == appEnum.Role.SuperAdmin;
  }
  isAdminRole() {
    const role = this.getUserRole();
    return role == appEnum.Role.Admin || role == appEnum.Role.SuperAdmin;
  }
  isNonAdminUser() {
    return this.isKalkylRole() || this.isInkopRole() || this.isClientRole();
  }
  isReadOnlyAccessRole() {
    return this.isKalkylRole() || this.isClientRole();
  }
  isCustomerUser() {
    const role = this.getUserRole();
    return role != appEnum.Role.Supplier && Object.values(appEnum.Role).includes(role);
  }

  getLoggedInUserDetail = () => {
    if (this.isSupplierSpecificRoute()) return this.state?.supplierToken
    return this.state?.token;
  }

  isGhostUser() {
    return this.state?.token?.isGhost;
  }

  fetchOrganizations = async () => {
    if (this.state.token?.id) {
      const orgData = await ServiceUser.getUserOrganizations(this.state.token.id);
      if (orgData?.[0]) {
        const { organizations } = orgData[0];
        this.setOrgData(organizations);
      }
    }
  }

  fetchAndSetToken = async (currentOrg, isOrgChangedToken = false) => {
    if (this.state.token?.id) {
      const { slug } = currentOrg;
      const orgToken = await ServiceUser.fetchAndSetToken(slug, isOrgChangedToken);
      if (orgToken) this.setTokenLocalStorage({...orgToken,slugFromUrl:slug});
    }
  }

  fetchUpdatedOrganizations = async () => {
    if (this.state.token?.id) {
      const orgData = await ServiceUser.getUserOrganizations(this.state.token.id);
      if (orgData?.[0]) {
        const { organizations } = orgData[0];
        this.persistObj("organizations", organizations);
        return organizations;
      }
    }
  }

  setOrgData = async (data) => {
    this.persistObj("organizations", data);
    this.setState({ organizations: data });
    const updatedCurrentOrg = data.find(org => org.slug == this.currentOrganization?.slug);
    this.currentOrganization = updatedCurrentOrg || data[0];
  }


  getCurrentOrg = () => {
    return this.currentOrganization || this.loadObj("token");
  }

  getCurrentOrgSlug = () => {
    return this.currentOrganization?.slug;
  }

  getOrgSlugFromUrl = () => {
    const requestedUrl = window.location.pathname;
    const [, slugFromUrl] = requestedUrl.split("/");
    return slugFromUrl || "";
  }

  isSuperAdminOrg = () => {
    const currentOrg = this.currentOrganization;
    return currentOrg && currentOrg.slug === 'superadmin'
  }

  getCurrentOrgName = () => {
    const currentOrg = this.currentOrganization || this.getOrgFromToken();
    return currentOrg?.name
  }

  updateChangedOrgSlug = (updatedOrgSlug) => {
    let _url = window.location.pathname.split('/');
    _url.splice(0,2);
    _url = _url.join("/");
    const updatedPath = `/${updatedOrgSlug}/${_url}`;
    history.replaceState(null, "", updatedPath);
  }

  getCurrentOrgUrl = async () => {
    const getDomainName = (url) => (new URL(url)).hostname;
    var [, slugFromUrl] = window.location.pathname.split("/");
    try {
      const { org, wasValid } = await serviceOrganization.validateAndGetOrgSlug(getDomainName(window.location.origin), slugFromUrl);
      slugFromUrl = org.slug;
      if (org.orgSlugFromHistory) this.updateChangedOrgSlug(slugFromUrl);
      if (org.slug) this.orgSlug = `/${slugFromUrl}`; //TODO: I think we can remove it

      let currentOrg;
      await this.fetchOrganizations();
      const organizations = this.loadObj('organizations');
      if (!slugFromUrl && this.state.token && this.state.token.role === appEnum.Role.SuperAdmin) {
        currentOrg = organizations.find(({slug}) => slug === "superadmin");
      } else {
        currentOrg = this.currentOrganization || this.getOrgFromToken() || organizations?.[0];
      }
      this.setCurrentOrganization(currentOrg);
      if (slugFromUrl === "error") {
        slugFromUrl = currentOrg?.slug;
      }
      if (slugFromUrl && currentOrg && (slugFromUrl != currentOrg.slug)) {
        const orgExist = organizations.find(o => o.slug === slugFromUrl);
        currentOrg = orgExist;
        if (orgExist) {
          this.setCurrentOrganization(currentOrg);
        }
        this.fetchAndSetToken({slug: slugFromUrl});
      }
      this.orgSlug = currentOrg && `/${currentOrg.slug}`;

      if (!this.orgSlug && org) {
        this.orgSlug = `/${org.slug}`;
        this.setCurrentOrganization(org);
        this.fetchAndSetToken({slug: org.slug});
      }

      return {slug: this.orgSlug, wasValid}
    } catch (error) {
      const errorStatus = error?.response?.status;
      if (errorStatus === 401) {
        this.signout();
        location.reload();
      }
      return { slug: null, wasValid: false };
    }
  }
  _getCurrentOrgUrl() {
    const [, slugFromUrl] = window.location.pathname.split("/");
    return this.orgSlug ? this.orgSlug : slugFromUrl ? `/${slugFromUrl}` : "";
  }
  _getCurrentOrgSlug() {
    const [, slugFromUrl] = window.location.pathname.split("/");
    return this.orgSlug ? this.orgSlug.replace("/", "") : slugFromUrl || "";
  }

  getOrganizations = () => this.loadObj("organizations") || [];


  setCurrentOrganization = (currentOrganization) => {
    this.currentOrganization = currentOrganization
  }
  setAvatarFetchedState(fetchedState) {
    this.avatarUrlFetched = fetchedState;
  }
  getAvatarFetchedState() {
    return this.avatarUrlFetched
  }

  setAvatarUrl(url){
    const _token = this.loadObj("token");
    if(_token){
      _token.avatarUrl = url;
      this.persistObj("token", _token);
      this.setState((prevState)=> ({...prevState,token:{...prevState.token,avatarUrl:url}}))
    }
  }

  getAvatarUrl(){
    return this.state.token?.avatarUrl || "";
  }
  getAvatarPath() {
    return this.state.token?.imgId || "";
  }
  getOrgSlugFromToken = () => this.state.token?.orgSlug;

  getOrgFromToken = () => {
    const orgSlugFromToken = this.state.token?.orgSlug
    const localOrganizations = this.loadObj('organizations');
    if (orgSlugFromToken && localOrganizations) {
      return localOrganizations.find(o=> o.slug === orgSlugFromToken);
    }
  }

  getOrgFromUrl = () => {
    const orgSlugFromUrl = this.getOrgSlugFromUrl()
    const localOrganizations = this.loadObj('organizations');
    if (orgSlugFromUrl && localOrganizations) {
      return localOrganizations.find(o=> o.slug === orgSlugFromUrl);
    }
  }

  getUpdatedRoleOrganizations = (organizations, role) => {
    const currentOrgSlug = this.getCurrentOrgSlug();
    const updatedOrgIndex = organizations.findIndex(o => o.orgId.slug === currentOrgSlug);
    organizations[updatedOrgIndex].role = role;
    return organizations;
  }

  getUserDetails = async () => {
    if (this.isSupplierSpecificRoute()) return;
    const userId = this.getUserId();
    const localToken = this.loadObj("token");
    if (userId) {
      try {
        const user = await ServiceUser.getUserById(userId);
        const _user = this.state.token;
        _user.avatarUrl = user.avatarUrl;
        _user.name = user.name;
        _user.phone = user.phone;
        // Merge preferences instead of replacing (preserve local projectOverview changes)
        // Local projectOverview takes precedence to preserve unsaved sorting preferences
        _user.preferences = user.preferences;
        localToken.avatarUrl = user.avatarUrl;
        localToken.name = user.name;
        localToken.phone = user.phone;
        // Keep local projectOverview preferences (don't overwrite with server data)
        localToken.preferences = user.preferences;
        this.setState({ token: _user });
        this.persistObj('token', localToken);
        Sentry.setUser({ id: userId, email: _user.email });
        // if (window.recapt && !appState.isTest()) {
        //   window.recapt("identify", {
        //     uid: userId,
        //     email: _user.email,
        //     nickname: user.name,
        //   });
        // }
        if (window.Beamer && appState.isTest()) {
          window.Beamer.update({ user_id: userId, user_email: _user.email});
        }
        return _user;
      } catch (error) {
        console.log("could not fetch user", error);
        return null;
      }
    }
  }
  getProjectFilterPreferences = () =>{
    const userData = this.state.token.preferences;
    return userData?.projectFilter
  }

  getProjectListPreferences = () => {
    return this.state.token.preferences?.projectTable && this.state.token.preferences.projectTable.length > 0 && this.state.token.preferences.projectTable
  }

  getProjectOverviewPreferences = () => {
    return this.state.token.preferences?.projectOverview;
  }

  setProjectOverviewPreferences = (data) => {
    const updatedToken = { ...this.state.token, preferences: { ...this.state.token.preferences, projectOverview: data } };
    this.persistObj("token", updatedToken);
    this.setState({ token: updatedToken });
  }

  getDashboardOverviewPreferences = () => {
    return this.state.token.preferences?.dashboard?.overview;
  }

  setDashboardOverviewPreferences = (data) => {
    this.setState({ token: {...this.state.token, preferences: { ...this.state.token.preferences, dashboard: { ...this.state.token.preferences?.dashboard, overview: data } }} })
  }

  getSummationCardsPreferences = () => {
    return this.state.token.preferences?.summationCards;
  }

  setSummationCardsPreferences = (data) => {
    this.setState({ token: {...this.state.token, preferences: { ...this.state.token.preferences, summationCards: data } }})
  }
  getListViewBetaPreference = () => {
    // return this.state.token?.preferences?.listViewBeta?.enabled ?? false;
    return true;
  }

  getUserToken = () => this.getLoggedInUserDetail()?.accessToken;
  getHvdToken = () => this.getLoggedInUserDetail()?.hvdToken;

  isSupplierSpecificRoute = () => {
    const supplierSpecificUrls = ['quotation', 'conversation'];
    const splittedUrl = window.location.pathname.split('/');
    return supplierSpecificUrls.some(url => splittedUrl[2]?.includes(url));
  }
  getUserName = () => this.getLoggedInUserDetail()?.name;

  getSupplierApplicationCount = () => this.state.supplierApplicationCount;

  setSupplierApplicationCount = (count) => this.setState({supplierApplicationCount: count*1});

  setAutosavingState = (value) => {
    this.setState({ autoSavingState: value })
    clearTimeout(this.#timeout);
    if (value === SavingState.DONE) {
      this.#timeout = setTimeout(() => this.setState({ autoSavingState: SavingState.HIDDEN }), 3000);
    }
  };
  getAutosavingState = () => this.state.autoSavingState;

  setGlobalNotifications = (globalNotifications) => this.setState({ globalNotifications: {...this.state.globalNotifications, ...globalNotifications} });

  getGlobalNotifications = () => this.state.globalNotifications;

  refreshProjectNotifications = async () => {
    const projectNotifications = this.isClientRole() ? 0 : (await ServiceProjects.getCounter())?.count;
    this.setGlobalNotifications({ project: projectNotifications });
  }

  getActiveProducts = (org) => {
    const organization = org ?? this.getCurrentOrg();
    const products = organization?.activeProducts ?? [];
    return products.length === 0 ? ["PURCHASING"] : products;
  };
  isPurchasingActive = (org) => this.getActiveProducts(org).includes("PURCHASING");
  isSalesActive = (org) => this.getActiveProducts(org).includes("SALES");

  setSupplierProfileHasBeenUpdated = (supplierProfileHasBeenUpdated) => this.setState({supplierProfileHasBeenUpdated})
  hasSupplierProfileBeenUpdated = () => this.state.supplierProfileHasBeenUpdated;

  setTeamComplete = (teamComplete) => this.setState({teamComplete})
  isTeamComplete = () => this.state.teamComplete ?? false;

  setInvoiceEmail = (email) => this.setState({invoiceEmail: email})
  getInvoiceEmail = () => this.state.invoiceEmail ?? this.getCurrentOrg()?.invoiceEmail ?? '';

  setInvoiceRef = (ref) => this.setState({invoiceRef: ref})
  getInvoiceRef = () => this.state.invoiceRef ?? this.getCurrentOrg()?.invoiceRef ?? '';

  setLogoUrl = (logoUrl) => this.setState({ logoUrl });
  getLogoUrl = () => this.state.logoUrl;

  setShowMassMessagingModal = (value) => this.setState({ showMassMessagingModal: value });
  getShowMassMessagingModal = () => this.state.showMassMessagingModal;

  setProductId = (id) => {
    this.setState({ productId: id });
  };
  getProductId = () => this.state.productId;

  getOrCreateAnonymousUUID = () => {
    if (!this.state.anonymousUUID) {
      const uuid = crypto.randomUUID();
      this.setState({ anonymousUUID: uuid });
      return uuid;
    } else {
      return this.state.anonymousUUID;
    }
  }
  setAnonymousUUID = (uuid) => {
    this.setState({ anonymousUUID: uuid });
  }

  getUserNotificationPreferences = () => {
    return this.state.token?.preferences?.notifications ?? {};
  }

  // For a flag the backend has already written as part of another request, so the toggle on
  // the profile page reflects it without a second round trip that would set it again.
  setUserNotificationPreference = (notificationName, value) => {
    this.setState({
      token: {
        ...this.state.token,
        preferences: {
          ...this.state.token?.preferences,
          notifications: {
            ...this.state.token?.preferences?.notifications,
            [notificationName]: value,
          },
        },
      },
    });
  }

  updateUserNotificationPreferences = (notificationName, value) => {
    autosave(async () => {
      await ServiceUser.updatePreferences(appState.getUserId(), value, `notifications.${notificationName}`);
      this.setUserNotificationPreference(notificationName, value);
    });
  }

  isTest = () => window.location.hostname.startsWith("test") || window.location.hostname.startsWith("localhost");
}

const appState = new AppStateContainer();

window.APP_STATE = appState;

export default appState;
